import { useCallback, useState } from "react";
import _ from "lodash";

import { CloseIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { batchedPromises } from "utils/common";
import { handleInvalidRequest } from "utils/sdk";

import Alert from "components/Alert";
import Button from "components/Button";
import IconButton from "components/IconButton";
import Stack from "components/Stack";
import Text from "components/Text";
import UploadButton from "components/UploadButton";
import { defaultDragAndDropZone } from "components/UploadButton/utils";
import { useLoadingBackdrop } from "components/useLoadingBackdrop";

import { upload } from "entities/File/sdk";
import { superuserChecklistFileImportCreate } from "entities/Superuser/sdk";

export interface IFileUpload {
  handleFilesUpload: ({
    checklistFileImportId,
  }: {
    checklistFileImportId: number;
  }) => void;
  onStepBack: () => void;
  selectedDealId: number;
  selectedUserId: number;
}

const FileUpload = ({
  handleFilesUpload,
  onStepBack,
  selectedDealId,
  selectedUserId,
}: IFileUpload) => {
  const [selectedFiles, setSelectedFiles] = useState<Array<File>>([]);
  const [showLoading, hideLoading] = useLoadingBackdrop();

  const handleFilesSelect = useCallback(
    ({ files }: { files: Array<File> }) => {
      setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
    },
    [setSelectedFiles]
  );

  const handleChecklistFileImportCreate = useCallback(() => {
    if (!_.isNil(selectedDealId) && !_.isNil(selectedUserId)) {
      showLoading();

      const fileIds: Array<number> = [];
      const uploadPromises = selectedFiles.map(
        (file) => () =>
          upload({ file, isPrivate: true })
            .then((fileId) => {
              fileIds.push(fileId);
            })
            .catch(handleInvalidRequest)
      );

      batchedPromises({
        promises: uploadPromises,
      }).then(() => {
        superuserChecklistFileImportCreate({
          dealId: selectedDealId,
          fileIds,
          valueCreatorId: selectedUserId,
        })
          .then(({ id }) => {
            handleFilesUpload({ checklistFileImportId: id });
          })
          .finally(() => hideLoading());
      });
    }
  }, [
    selectedFiles,
    selectedDealId,
    selectedUserId,
    showLoading,
    hideLoading,
    handleFilesUpload,
  ]);

  const clearFile = useCallback(
    ({ fileToRemove }: { fileToRemove: File }) => {
      setSelectedFiles((prevFiles) =>
        prevFiles.filter((file) => file !== fileToRemove)
      );
    },
    [setSelectedFiles]
  );

  return (
    <Stack
      spacing={1}
      sx={{
        height: "calc(100vh - 245px)",
      }}
      marginTop={1}
    >
      <Alert severity="info">
        Upload the files that you would like to import into checklists.
      </Alert>

      <Stack
        sx={{
          flexGrow: 1,
          minHeight: "110px",
          display: "grid",
          gridGap: theme.spacing(2),
          gridTemplateColumns: "1fr 1fr",
        }}
      >
        <UploadButton
          onChange={handleFilesSelect}
          renderComponent={(state) =>
            defaultDragAndDropZone(
              state,
              "Choose files or drag and drop them here",
              {
                height: "100%",
              }
            )
          }
        />
        <Stack
          spacing={2}
          padding={theme.spacing(0, 2, 2, 2)}
          sx={{
            border: `1px dotted ${colors.gray40}`,
            borderRadius: "8px",
            maxHeight: "inherit",
            overflowY: "auto",
          }}
        >
          {!_.isEmpty(selectedFiles) && (
            <Stack
              sx={{
                position: "sticky",
                top: 0,
                backgroundColor: colors.white,
                paddingTop: 2,
                zIndex: 2,
                borderBottom: `1px solid ${colors.gray40}`,
              }}
            >
              <Text variant="h6">
                {`${selectedFiles.length} file${selectedFiles.length > 1 ? "s" : ""} uploaded`}
              </Text>
            </Stack>
          )}

          {!_.isEmpty(selectedFiles) && (
            <Stack spacing={1}>
              {selectedFiles.map((file, index) => (
                <Stack
                  spacing={1}
                  key={`${file.name}-${index}`}
                  alignItems="center"
                  justifyContent="space-between"
                  direction="row"
                  sx={{
                    transition: "background-color 0.2s ease",
                    "&:hover": {
                      backgroundColor: colors.gray5,
                    },
                  }}
                >
                  <Text variant="text2">{file.name}</Text>

                  <IconButton onClick={() => clearFile({ fileToRemove: file })}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" justifyContent="space-between">
        <Button onClick={onStepBack}>Back</Button>

        <Button
          disabled={_.isEmpty(selectedFiles)}
          onClick={handleChecklistFileImportCreate}
        >
          Next
        </Button>
      </Stack>
    </Stack>
  );
};

export default FileUpload;
